
"use client";

import React, { useEffect, useRef, useState } from 'react'

import getImageURL from 'bwax-ui/getImageURL'

import { renderVideo, renderAudio as renderAudioComp } from './editor/utils/MediaHelper'
import { isQQPlayURL } from 'bwax-ui/auxiliary/richtext/mediaTypes'
import { ratioOuterStyle, ratioLOCALOuterStyle } from './editor/components/styles'
import { isWeChat } from 'bwax-ui/clientEnv'
import Checkbox from '../../components/inputs/Checkbox';
import styleClasses from '../richtext_slate/editor/SlateEditor.module.css'
import AutoPlayVideo from "bwax-ui/components/AutoPlayVideo";

import Link from 'bwax-ui/page/Link';

import convertSlateToMarkdown from './editor/convertSlateToMarkdown'

import "../../basic/RichText.css"

import clsx from 'clsx'


function isList (nodeType) {
    const listTypes = ["numbered-list", "bulleted-list"]

    return listTypes.includes(nodeType)
}


export default function RichTextRenderer({ value, version, className, updateValue, outlineReady = _ => {}, keywords = {}, scrollContainer }) {

    const rendererRef = useRef(null)

    function render() {
        return value.map((node, index) => {
            const listInfo = isList(node.type) ? {
                type: node.type,
                level: 0
            } : undefined
            const path = [index]
            return renderRichTextElement(node, index, { components, containerRef: rendererRef }, path, listInfo, value, updateValue, keywords, scrollContainer);
        })
    }

    // useEffect(() => {
    //     const md = convertSlateToMarkdown(value)
    //     console.log(">> md: \n", md);

    // }, [value])
    

    useEffect(() => {
        if (isWeChat()) {
            let imgsArr = []
            const imgNodesArr = document.querySelectorAll('.rich-text img')
            imgNodesArr.forEach(img => {
                imgsArr.push(img.getAttribute('src'))
            })

            for (let i = 0; i < imgNodesArr.length; i++) {
                imgNodesArr[i].onclick = function () {
                    const currentImgUrl = imgNodesArr[i].getAttribute('src')
                    wx.previewImage({
                        current: currentImgUrl,
                        urls: imgsArr
                    })
                }
            }
        }
    }, [])


    useEffect(() => {
        if(value) {
            outlineReady(
                value.map((n, index) => {
                    if(["h2", "h3", "h4"].indexOf(n.type) == -1){
                        return null
                    }

                    const depth = parseInt(n.type.substring(1));
                    return {
                        depth,
                        text: n.children[0]?.text,
                        hid: "" + index
                    }
                }).filter(x => !!x)
            )
        }
    }, [ value ]);


    if (!value) {
        return (
            <div>no slate content</div>
        )
    }

    return (
        <div className={clsx('rich-text', className, { "rich-text-v2": version == 2 })} ref={rendererRef}>
            {render()}
        </div>
    )
}


function renderRichTextElement(node, key, { components, containerRef }, path, listInfo, value, updateValue, keywords, scrollContainer) {

    // closure
    const renderElement = (node, key) => {
        const newListInfo = listInfo ? {
            ...listInfo,
            level: node.type === listInfo.type ? listInfo.level + 1 : listInfo.level
        } : undefined
        const newPath = [...path, key]
        return renderRichTextElement(node, key, { components, containerRef }, newPath, newListInfo, value, updateValue, keywords, scrollContainer)
    }

    if (node.type == "link" || node.type == "break" || (node.text !== undefined)) {
        return <StyledText node={node} key={key} keywords={keywords} value={value} path={path}/>
    }

    const Comp = components[node.type];


    if (Comp) {
        return (
            <Comp node={node} key={key} containerRef={containerRef} renderElement={renderElement} path={path} 
                listInfo={listInfo} value={value} updateValue={updateValue} scrollContainer={scrollContainer}
            />
        )
    } else {
        return (
            <div key={key} style={{ padding: "0.5rem 0.5rem", backgroundColor: "var(--yellow-4)" }}>{`<不支持的富文本内容 ${node.type}>`}</div>
        )
    }
}


const Paragraph = ({ node, renderElement }) => {
    const { children, align } = node


    return (
        <p style={align ? { textAlign: align } : {}} >
            {children.map((c, idx) => renderElement(c, idx))}
        </p>
    )
}

const ListItem = ({ node, renderElement }) => {
    const { children } = node

    return (
        <li>
            {
                children.map((child, index) => {
                    return renderElement(child, index)
                })
            }
        </li>
    )
}

const NestWrapper = ({ node, renderElement }) => {
    const { children } = node
    
    return (
        <div className='nest-wrapper'>
            { children.map((child, index) => {
                return renderElement(child, index)
            })}
        </div>
    )
}

const ImageBlock = ({ node, containerRef }) => {
    const { url, width, float, caption } = node

    const [actualWidth, setActualWidth] = useState(width)

    useEffect(() => {
        /**
         * https://git.qunfengshe.com/qunfengshe/bwax-app-admin/-/issues/1147
         * 如果 width 是 px，且比 renderer 宽度大，则设置为 100% (by 威豪 2022-12-16)
         */
        if (containerRef && containerRef.current && typeof (width) === "number" && width > containerRef.current.clientWidth) {
            setActualWidth('100%')
        } else {
            setActualWidth(width)
        }
    }, [containerRef, width])

    const alignStyle = float ? imageAlignStyle[float] : imageAlignStyle["center"]


    const imageWidth = actualWidth ? {
        width: actualWidth, maxWidth: "100%"
    } : {
        width: "100%", maxWidth: "32rem"  // 为了在 PC 端默认图片不要太大
    }

    const altProp = caption ? { alt: caption } : { alt: "Sample Image in Article"}

    // 
    return (
        <figure style={{ ...alignStyle, ...imageWidth }}>
            <img src={processImageURL(url, "large")} className="hidden sm:block" style={{ width: "100%"}} {...altProp} 
                // width={600} height={900}  // height 和 width 只用来占位
            />
            <img src={processImageURL(url, "medium")} className="block sm:hidden" style={{ width: "100%"}} {...altProp} 
                // width={600} height={900}  // height 和 width 只用来占位
            />
            { 
                caption ? (
                    <div className="text-[var(--gray-10)] text-center text-sm mt-1.5">
                        {caption}
                    </div>
                ) : null 
            }
        </figure>

    )
}

const Media = ({ node, scrollContainer }) => {
    const { type, url, align, autoPlay } = node

    function renderAudio() {
        return (
            <figure className='editor-audio-block' style={{ textAlign: align || 'left', padding: "0.75rem" }}>
                {renderAudioComp(url)}
            </figure>
        )
    }

    const videoContainerStyle = isQQPlayURL(url) ? ratioOuterStyle : ratioLOCALOuterStyle

    // 用 div 包含是为了避免在切换 video 播放器的时候页面报错的问题
    // https://www.youtube.com/watch?v=fsVT4joNw5M&ab_channel=ProfessourceCode
    return type === "video" ? (
        <figure style={videoContainerStyle}>
            { 
                autoPlay ? (
                    <AutoPlayVideo src={url} className="w-full" scrollContainer={scrollContainer} />
                ) : (
                    <div>
                        { renderVideo(url) }
                    </div>
                )
                    
            }
        </figure>
    ) : renderAudio()
}

const Blockquote = ({ node, renderElement }) => {
    const { children, align } = node

    const blockquoteRef = useRef(null)

    const [padding, setPadding] = useState('16px')
    const [quoteClassName, setQuoteClassName] = useState('')

    useEffect(() => {

        const { current } = blockquoteRef
        const { nextSibling, previousSibling } = current

        if (nextSibling && nextSibling.nodeName === "BLOCKQUOTE" && previousSibling && previousSibling.nodeName === "BLOCKQUOTE") {
            setPadding('0 16px')
            setQuoteClassName('middle-blockquote')
        } else if (nextSibling && nextSibling.nodeName === "BLOCKQUOTE") {
            setPadding('16px 16px 0')
            setQuoteClassName('first-blockquote')
        } else if (previousSibling && previousSibling.nodeName === "BLOCKQUOTE") {
            setPadding('0 16px 16px')
        } else {
            setPadding('16px')
            setQuoteClassName('single-blockquote')
        }
    })

    return (
        <blockquote ref={blockquoteRef} style={{ padding, textAlign: align }}
            className={quoteClassName}
        >
            {children.map((node, idx) => {
                return renderElement(node, idx)
            })}
        </blockquote>
    )
}

const heading = depth => ({ node, renderElement, path }) => {
    const { children } = node

    const attrs = { "data-hid": path.join("-"), "data-heading": true }
    return React.createElement(
        "h" + depth,
        attrs, 
        children.map(renderElement)
    )
};

function getListStyleType (node, listInfo, listType) {
    const { listStyleType } = node
    if(listStyleType) {
        return { listStyleType: listStyleType }
    } else if (listInfo) {
        const styleTypes = {
            'numbered-list': ['decimal', 'lower-alpha', 'lower-roman'],
            'bulleted-list': ['disc', 'circle', 'square']
        }
        return { listStyleType: styleTypes[listType][listInfo.level % 3] }
    } else return {}
    
}

const TodoListItem = ({ node, renderElement, path, value, updateValue }) => {
    const { children, checked } = node

    const [checkedFlag, setCheckedFlag] = useState(false)

    useEffect(() => {
        setCheckedFlag(checked)
    }, [checked])

    return (
        <div className={clsx(styleClasses['todo-list-item'], 'flex items-center gap-1.5 py-[3px]')
        }>
            <Checkbox color="blue" checked={checkedFlag} isDisabled={!updateValue}
                onChange={checked => {
                    console.log("checked: ", checked);
                    if(updateValue) {
                        updateValue(value, { checked }, path)
                    }
                }}
            />
            {
                children.map((child, index) => {
                    return renderElement(child, index)
                })
            }
        </div>
    )
}


const components = {
    "paragraph": Paragraph,

    "h1": heading(1),
    "h2": heading(2),
    "h3": heading(3),
    "h4": heading(4),
    "h5": heading(5),
    "h6": heading(6),

    "list-item": ListItem,
    "nestWrapper": NestWrapper,

    "numbered-list": ({ node, renderElement, listInfo }) => {
        const { children } = node
        return (
            <ol style={getListStyleType(node, listInfo, 'numbered-list')}>
                {children.map(renderElement)}
            </ol>
        )
    },
    "bulleted-list": ({ node, renderElement, listInfo }) => {
        const { children } = node
        return (
            <ul style={getListStyleType(node, listInfo, 'bulleted-list')}>
                {children.map(renderElement)}
            </ul>
        )
    },
    "block-quote": Blockquote,
    "code": ({ node }) => {
        const { value } = node
        return (
            <pre className="lc-source-code">
                { value}
            </pre>
        )
    },

    "inline-code": ({ node }) => {
        const { value } = node;
        return (
            <code className="lc-inline-code">
                { value }
            </code>
        )
    },

    "html-tag": ({ node }) => {
        const { value } = node;
        return value
    },


    "hr": _ => <hr />,
    "image": ImageBlock,
    "link": ({ node }) => {
        const { children, url, targetBlank } = node
        // 目前只有包含 image 的 link 会在第一级

        return (
            <Link to={url} openWithNewTab={targetBlank}>
                {children.map(renderElement)}
            </Link>
        )

        // return (
        //     <a href={url} target={targetBlank ? '_blank' : '_self'}>
        //         {children.map(renderElement)}
        //     </a>
        // )
    },

    "table": ({ node, renderElement }) => {
        const { children } = node;
        return (
            <div className="table-wrapper">
                <table>
                    <tbody>
                        {children.map(renderElement)}
                    </tbody>
                </table>
            </div>
        )
    },

    // table structure in struction is like： https://github.com/ianstormtaylor/slate/blob/main/site/examples/tables.tsx
    "table-row": ({ node, renderElement }) => (
        <tr>
            {node.children.map(renderElement)}
        </tr>
    ),

    "table-cell": ({ node, renderElement }) => {
        const { width } = node;
        // width 会设置在 cell 级别吗, 还是应该设置在 table 级别，使用 <colgroup> 标签？
        return (
            <td style={ width ? { minWidth: width, maxWidth: width } : {}}>
                {node.children.map(renderElement)}
            </td>
        )

    },
    
    "todo-list": ({ node, renderElement, listInfo }) => {
        const { children } = node
        return (
            <div>
                {children.map(renderElement)}
            </div>
        )
    },
    "todo-list-item": TodoListItem,


    "video": Media,
    "audio": Media
}




function StyledText({ node, keywords, value, path }) {

    const keywordRegxsArr = Object.keys(keywords).reduce((acc, keyword) => {
        const keywordArrSplitBySpace = keyword.split(' ').filter(k => !!k) // 去掉空格，只留下文本内容

        // 将文本内容和匹配若干个空格的正则组合起来，i: 不区分大小写
        const keywordRegx = new RegExp(`${keywordArrSplitBySpace.join('\\s*')}`, 'i')

        return acc.concat(keywordRegx)
    }, []) // 每一项都是 keyword 的 regx

    function hasKeywords (text) {
        // search：如果匹配成功，则返回正则表达式在字符串中首次匹配的索引；否则，返回 -1。
        return keywordRegxsArr.some(regx => text.search(regx) !== -1) 
    }

    function divideTextWithKeyword (leaf) {
        const { color, background } = leaf
        const originalText = leaf.text

        if(hasKeywords(originalText)) {
            // 将 text 分割成若干段，将 keyword 部分独立出来
            const matchedKeywordRegxs = keywordRegxsArr.filter(regx => originalText.search(regx) !== -1)

            function divide (text, regx, count = 0) { // count: 表示在 originalText 中第{count}次处理此 regx
                const matchResult = text.match(regx) // array or null
                if(matchResult) {
                    
                    let dividedTextArr = []
                    const matchedTextContnet = matchResult[0]
                    const keywordLength = matchedTextContnet.length
                    const startIndex = matchResult['index']

                    // 判断 matchedTextContnet 是否是全文的第一个，是则替换
                    const previousContent = value.reduce((acc, node, index) => {
                        function resolveText(node, p) {
                            if (node.children && node.children.length > 0) {
                                return node.children.map((child, idx) => {
                                    return resolveText(child, [...p, idx])
                                }).join('')
                            } else {
                                // text node
                                const min = Math.min(p.length, path.length)
                                let textContent = ""
                                for (let i = 0; i < min; i++) {
                                    if (p[i] < path[i]) {
                                        textContent = node.text
                                        break
                                    } 
                                    if (p[i] > path[i]) {
                                        // textContent = ''
                                        break 
                                    }
                                    if (p[i] === path[i]) {
                                        const gRegx = new RegExp(regx, 'ig')
                                        const matchResults = [...originalText.matchAll(gRegx)]
                                        textContent = originalText.substring(0, matchResults[count]['index'])
                                        break
                                    }
                                } 
                                return textContent

                            }
            
                        }

                        return acc + resolveText(node, [index]);
                    }, '')

                    const isFirstTextContent = !previousContent.includes(matchedTextContnet)
                    
                    if(isFirstTextContent) {
                        if(startIndex !== 0) {
                            dividedTextArr.push({ textContent: text.substring(0, startIndex), isKeyword: false })
                        }

                        dividedTextArr.push({ 
                            textContent: text.substring(startIndex, startIndex + keywordLength), 
                            isKeyword: true 
                        })

                        if(startIndex + keywordLength !== text.length) {
                            const dividedRemainText = divide(text.substring(startIndex + keywordLength), regx, count + 1)
                            dividedTextArr = dividedTextArr.concat(dividedRemainText)
                            // dividedTextArr.push({ textContent: text.substring(startIndex + keywordLength), isKeyword: false })
                        }
                    } else {
                        dividedTextArr.push({ textContent: text, isKeyword: false })
                    }

                    // console.log("dividedTextArr: ", dividedTextArr, originalText, path);

                    return dividedTextArr
                } else {
                    return [{ textContent: text, isKeyword: false }]
                }
                
            }

            const dividedTextArr = matchedKeywordRegxs.reduce((acc, regx, index) => {
                if(index === 0) {
                    return acc.concat(divide(originalText, regx))
                } else {
                    return acc.flat().map(dividedTextObj => {
                        const { textContent, isKeyword } = dividedTextObj
                        if(!isKeyword && textContent) {
                            return divide(textContent, regx)
                        } else {
                            return { textContent, isKeyword }
                        }
                    })
                }
            }, []).flat()

            // console.log("dividedTextArr: ", dividedTextArr);
            
            return (
                <>
                {
                    dividedTextArr.map(({ textContent, isKeyword }, index) => {
                        if(!isKeyword) {
                            return textContent
                        } else {
                            const url = (() => {
                                const keywordRegx = keywordRegxsArr.find(regx => !!textContent.match(regx))
                                const findedKey = Object.keys(keywords).find(key => !!key.match(keywordRegx))
                                return keywords[findedKey]
                            })()
                            return (
                                <Link key={index} to={url} style={color || background ? { color, background } : {}}>
                                    {textContent}
                                </Link>
                            )
                        }
                    })
                }
                </>
            )
            
        } 

        return originalText
    }

    function renderLeaf(leaf, isInLink = false) {
        const { css } = leaf
        let leafElement = leaf.text !== "" ? 
            (css ? <span style={css}>{isInLink ? leaf.text : divideTextWithKeyword(leaf)}</span> : 
                isInLink ? leaf.text : divideTextWithKeyword(leaf)) : 
            <br />

        if (leaf.bold) {
            leafElement = <strong>{leafElement}</strong>
        }

        if (leaf.code) {
            leafElement = <code>{leafElement}</code>
        }

        if (leaf.italic) {
            leafElement = <em>{leafElement}</em>
        }

        if (leaf.underline) {
            leafElement = <u>{leafElement}</u>
        }

        if (leaf.sub) {
            leafElement = <sub>{leafElement}</sub>
        }

        if (leaf.sup) {
            leafElement = <sup>{leafElement}</sup>
        }

        return leaf.color || leaf.background ? (
            <span style={{ color: leaf.color, background: leaf.background }}>{leafElement}</span>
        ) : leafElement
    }

    if (node.type && node.type === "link") {
        
        const { url } = node;
        const target = (() => {
            if(url.startsWith("http")){
                return "_blank"; // 绝对路径总是直接打开。 TODO 有可能要 confirm
            }
            return node.targetBlank ? '_blank' : '_self';
        })();

        return (
            <Link to={url} openWithNewTab={target}>
                {node.children.map((child, idx) => <React.Fragment key={idx}>{renderLeaf(child, true)}</React.Fragment>)}
            </Link>
        )
    } else if (node.type == "break") {
        return <br />

    } else {
        return renderLeaf(node)
    }
}


/// for images
export const imageAlignStyle = {
    "center": { marginLeft: "auto", marginRight: "auto" },
    "left": { float: "left", marginRight: 4 },
    "right": { float: "right", marginLeft: 4 }
}


export function processImageURL(urls, size = "large") {
    function processForDataBlob(url) {
        if (!url) {
            return url
        }
        const isObjectURL = url.match(/^blob:.+/) !== null
        if (isObjectURL) {
            return url
        } else {
            return getImageURL({ url }, size)
            // 使用 nextjs 
            // return url
        }
    }
    if (urls && Array.isArray(urls)) {
        return urls.map(processForDataBlob);
    } else {
        return processForDataBlob(urls)
    }

}
